/**
 * Server-sent events over fetch.
 *
 * The browser's own `EventSource` is not usable here. It cannot set request
 * headers, and the API authenticates the chat stream with a bearer token like
 * every other endpoint: there is no query-parameter token and there should not
 * be one, because a token in a URL ends up in access logs, in the Referer
 * header and in browser history.
 *
 * So the stream is read with fetch, which can carry the Authorization header
 * and, on reconnection, the Last-Event-ID header the API uses to replay what
 * was missed. That leaves this module owing three things EventSource would
 * otherwise have provided: frame parsing, reconnection, and the retry interval.
 *
 * The wire format is the one in the specification, and the parser follows it
 * rather than approximating it:
 *
 *   - Fields are `name: value`, with one optional leading space stripped.
 *   - A line beginning with `:` is a comment. The API sends these as
 *     heartbeats, and receiving one is proof the connection is alive.
 *   - Several `data:` lines in a frame join with a newline between them.
 *   - A blank line dispatches the frame. `event:` names it, defaulting to
 *     "message"; `id:` becomes the reconnection cursor; `retry:` sets the
 *     reconnection delay in milliseconds.
 *   - Frames may be split across reads at any byte, so the decoder is
 *     streaming and the buffer is carried between chunks.
 */

export interface SSEEvent {
    /** The `event:` field, or "message" when the frame did not name one. */
    event: string;
    /** All `data:` lines of the frame, joined with newlines. */
    data: string;
    /** The `id:` field, if the frame carried one. */
    id?: string;
}

export interface SSEHandlers {
    /** Called once per dispatched frame. Comments and heartbeats never reach it. */
    onEvent: (event: SSEEvent) => void;
    /** Called when a connection is established, including on every reconnect. */
    onOpen?: () => void;
    /**
     * Called when a connection ends or fails. `willRetry` says whether this
     * client is about to reconnect, so a UI can distinguish "reconnecting" from
     * "gave up".
     */
    onError?: (error: unknown, willRetry: boolean) => void;
}

export interface SSEOptions extends SSEHandlers {
    url: string;
    /** Bearer token. Read at connect time so a refresh is picked up on reconnect. */
    token: () => string | null;
    /** Milliseconds. The server's `retry:` field overrides this when it sends one. */
    initialRetryMs?: number;
    /** Ceiling for the backoff, so a long outage does not push retries hours apart. */
    maxRetryMs?: number;
}

/** Parses a complete frame's raw text into an event, or null if it carried no data. */
export function parseFrame(raw: string): SSEEvent | null {
    let event = "message";
    let id: string | undefined;
    const data: string[] = [];
    let sawData = false;

    for (const line of raw.split("\n")) {
        if (line === "" || line.startsWith(":")) continue;

        const colon = line.indexOf(":");
        const field = colon === -1 ? line : line.slice(0, colon);
        let value = colon === -1 ? "" : line.slice(colon + 1);
        // Exactly one leading space is part of the framing, not the value.
        if (value.startsWith(" ")) value = value.slice(1);

        switch (field) {
            case "event":
                event = value;
                break;
            case "data":
                data.push(value);
                sawData = true;
                break;
            case "id":
                // A NUL in an id is required to be ignored rather than stored.
                if (!value.includes("\0")) id = value;
                break;
            default:
                // `retry` is handled by the caller, which owns the delay.
                break;
        }
    }

    if (!sawData) return null;
    return { event, data: data.join("\n"), id };
}

/** Reads a `retry:` field out of a raw frame, in milliseconds. */
export function parseRetry(raw: string): number | null {
    for (const line of raw.split("\n")) {
        if (!line.startsWith("retry:")) continue;
        const value = line.slice("retry:".length).trim();
        if (!/^\d+$/.test(value)) continue;
        return Number(value);
    }
    return null;
}

/**
 * A stream connection that reconnects until it is closed.
 *
 * Returns a function that stops it. Calling that is not optional: without it
 * the fetch stays open, the reconnect timer keeps firing, and the server holds
 * a subscription for a client that is gone.
 */
export function connectSSE(options: SSEOptions): () => void {
    const {
        url,
        token,
        onEvent,
        onOpen,
        onError,
        initialRetryMs = 3000,
        maxRetryMs = 60000,
    } = options;

    let controller: AbortController | null = null;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let closed = false;
    let lastEventId: string | null = null;
    let retryMs = initialRetryMs;
    let attempt = 0;

    const scheduleReconnect = (error: unknown) => {
        if (closed) return;
        onError?.(error, true);
        // Exponential, capped, with jitter. Without jitter a server restart has
        // every client return in the same instant and knock it over again.
        const backoff = Math.min(retryMs * 2 ** attempt, maxRetryMs);
        const jittered = backoff * (0.5 + Math.random() * 0.5);
        attempt += 1;
        timer = setTimeout(() => void open(), jittered);
    };

    async function open(): Promise<void> {
        if (closed) return;

        controller = new AbortController();
        const headers: Record<string, string> = { Accept: "text/event-stream" };
        const bearer = token();
        if (bearer) headers.Authorization = `Bearer ${bearer}`;
        if (lastEventId) headers["Last-Event-ID"] = lastEventId;

        let response: Response;
        try {
            response = await fetch(url, {
                headers,
                signal: controller.signal,
                // Our own reconnection handles resumption; letting the browser
                // serve a cached body would replay a dead stream.
                cache: "no-store",
            });
        } catch (error) {
            if (closed) return;
            scheduleReconnect(error);
            return;
        }

        if (!response.ok || !response.body) {
            // 401 and 403 are not transient. Retrying a refused stream forever
            // is a busy loop against an endpoint that will keep saying no.
            if (response.status === 401 || response.status === 403) {
                onError?.(
                    new Error(`chat stream refused: ${response.status}`),
                    false
                );
                closed = true;
                return;
            }
            scheduleReconnect(
                new Error(`chat stream failed: ${response.status}`)
            );
            return;
        }

        // Connected. Reset the backoff so the next unrelated failure starts
        // from the bottom rather than from wherever the last outage ended.
        attempt = 0;
        onOpen?.();

        const decoder = new TextDecoder();
        let buffer = "";

        try {
            // Inside the try: getReader throws if the body is already locked or
            // disturbed, and an unhandled rejection there would kill the
            // connection with no reconnect.
            const reader = response.body.getReader();

            for (;;) {
                const { done, value } = await reader.read();
                if (done) break;

                buffer += decoder.decode(value, { stream: true });

                // Frames end at a blank line. Normalising line endings first
                // means a proxy that rewrites them cannot split a frame in a
                // way the parser does not recognise.
                buffer = buffer.replace(/\r\n|\r/g, "\n");

                let boundary = buffer.indexOf("\n\n");
                while (boundary !== -1) {
                    const raw = buffer.slice(0, boundary);
                    buffer = buffer.slice(boundary + 2);

                    const retry = parseRetry(raw);
                    if (retry !== null) retryMs = retry;

                    const event = parseFrame(raw);
                    if (event) {
                        if (event.id) lastEventId = event.id;
                        onEvent(event);
                    }
                    boundary = buffer.indexOf("\n\n");
                }
            }
            // A clean end of body is still a disconnection: the server closed
            // the stream, most likely on its own lifetime limit, and the client
            // is expected to come back.
            scheduleReconnect(new Error("chat stream ended"));
        } catch (error) {
            if (closed) return;
            scheduleReconnect(error);
        }
    }

    void open();

    return () => {
        closed = true;
        if (timer) clearTimeout(timer);
        controller?.abort();
    };
}

export default connectSSE;
